/**
 * Pure date/time helpers for the showings setup. Calendar dates are `YYYY-MM-DD` strings handled with
 * UTC arithmetic only (no browser-local `Date` math, see docs/claude/timezone-policy.md); times of day
 * are `HH:MM` 24-hour strings, the shape the availability API stores.
 */

import { asCalendarDateString, formatCalendarDate } from '@resida/shared';

export const WEEKDAY_NAMES: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

export function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

/** "17:00" -> "5:00 pm", "09:30" -> "9:30 am". */
export function formatTimeOfDay(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
}

export function formatTimeRange(startTime: string, endTime: string): string {
  return `${formatTimeOfDay(startTime)} – ${formatTimeOfDay(endTime)}`;
}

/** Half-hour choices from 6:00 am to 11:30 pm; a stored value off that grid is added so it still shows. */
export function timeOptions(current?: string): { value: string; label: string }[] {
  const values: string[] = [];
  for (let minutes = 6 * 60; minutes <= 23 * 60 + 30; minutes += 30) {
    values.push(`${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`);
  }
  if (current && !values.includes(current)) values.push(current);
  return values.sort().map((value) => ({ value, label: formatTimeOfDay(value) }));
}

// ---------------------------------------------------------------------------
// Calendar dates (YYYY-MM-DD)
// ---------------------------------------------------------------------------

export function toDateKey(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseDateKey(key: string): { year: number; month: number; day: number } {
  const [year, month, day] = key.split('-').map(Number) as [number, number, number];
  return { year, month, day };
}

export function addDays(key: string, days: number): string {
  const { year, month, day } = parseDateKey(key);
  const d = new Date(Date.UTC(year, month - 1, day + days));
  return toDateKey(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/** ISO weekday of a calendar date: 1 = Monday .. 7 = Sunday. */
export function isoWeekdayOf(key: string): number {
  const { year, month, day } = parseDateKey(key);
  return ((new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7) + 1;
}

/** Every date from `from` to `to` inclusive (empty when `to` is before `from`). */
export function datesBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let key = from; key <= to; key = addDays(key, 1)) out.push(key);
  return out;
}

export function monthStartKey(key: string): string {
  const { year, month } = parseDateKey(key);
  return toDateKey(year, month, 1);
}

export function addMonths(monthKey: string, months: number): string {
  const { year, month } = parseDateKey(monthKey);
  const d = new Date(Date.UTC(year, month - 1 + months, 1));
  return toDateKey(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
}

/** Weeks (Sunday first) covering the month that starts at `monthKey`; days outside the month are null. */
export function monthGrid(monthKey: string): (string | null)[][] {
  const { year, month } = parseDateKey(monthKey);
  const first = new Date(Date.UTC(year, month - 1, 1));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells: (string | null)[] = Array.from({ length: first.getUTCDay() }, () => null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(toDateKey(year, month, day));
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export function formatDateKey(key: string, options: Intl.DateTimeFormatOptions): string {
  return formatCalendarDate(asCalendarDateString(key), options);
}

/** "Tue, Nov 3" */
export function formatShortDay(key: string): string {
  return formatDateKey(key, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** "November 2026" */
export function formatMonthLabel(monthKey: string): string {
  return formatDateKey(monthKey, { month: 'long', year: 'numeric' });
}

/** The `YYYY-MM-DD` of an availability exception `date` (the API may send a full ISO string). */
export function exceptionDateKey(date: string): string {
  return date.slice(0, 10);
}

// ---------------------------------------------------------------------------
// Instants in the listing's timezone
// ---------------------------------------------------------------------------

/** The calendar date and `HH:MM` wall-clock time an ISO instant has in `timeZone`. */
export function instantToZonedParts(iso: string, timeZone: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(iso));
  const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? '00';
  return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
}
